import { useEffect, useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { useGetMe } from '../../api/generated/identity/identity';
import {
  getListClassSeriesQueryKey,
  listClassSeries,
  useCreateClassSeries,
} from '../../api/generated/scheduling/scheduling';
import {
  getListEnrollmentsQueryKey,
  getListTeachersQueryKey,
  listEnrollments,
  listTeachers,
} from '../../api/generated/enrollment/enrollment';
import type { ClassSeries, Enrollment, Me, TeacherAdminDetail } from '../../api/generated/model';
import { Alert } from '../../design/primitives/Alert';
import { Button } from '../../design/primitives/Button';
import { Field } from '../../design/primitives/Field';
import { SettledListView, settledList } from '../schedule/settledList';
import { isKnownTimezone } from '../schedule/wallClock';
import styles from './series.module.css';

/** Domingo e zero, como o contrato define. A ordem e a do `weekday`, nao a local. */
const WEEKDAYS = [0, 1, 2, 3, 4, 5, 6] as const;

/** Teto por pagina pedido ao servidor. O contrato fixa 200 como maximo. */
const PAGE_LIMIT = 200;

/**
 * A LISTA INTEIRA, e nao a primeira pagina dela — as tres desta tela.
 *
 * As tres chamadas saiam sem `limit` e sem `cursor`, e as tres param em 50 no
 * servidor: `seriesPageSize` (scheduling/series.go, `def = 50`) para a grade, e
 * `defaultPageSize` (enrollment/service.go) para professores e matriculas. Nada
 * lia `nextCursor`, e nada dizia que havia mais — nem o aviso de truncamento que
 * a tela vizinha de matriculas ao menos mostra.
 *
 * Nos dois seletores isso nao e "ver menos": e o Super Admin abrindo o
 * formulario, nao achando o professor 51 nem a matricula 51, e concluindo que
 * eles nao estao cadastrados. A serie daquele aluno deixa de existir por uma
 * ausencia que a tela nao explica — e serie e o unico caminho da interface para
 * montar a grade recorrente de alguem. Na lista, a grade da escola, que e o
 * cadastro que mais cresce, aparecia parcial e com cara de completa.
 *
 * Seguir o cursor ate `nextCursor` nulo, e nao um "carregar mais": o contrato
 * nao oferece busca por nome nestas operacoes — so `cursor` e `limit` —, entao
 * percorrer tudo e o unico jeito de a lista ser a lista.
 */
function useWholeList<TPage, TItem>(
  queryKey: readonly unknown[],
  fetchPage: (cursor: string | undefined, signal: AbortSignal) => Promise<TPage>,
  read: (page: TPage) => { readonly items: readonly TItem[]; readonly nextCursor: string | null },
  enabled = true,
): {
  readonly items: readonly TItem[] | undefined;
  readonly isError: boolean;
  readonly refetch: () => void;
} {
  const query = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam, signal }) => fetchPage(pageParam, signal),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last: TPage) => read(last).nextCursor ?? undefined,
    enabled,
  });

  const { hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage } = query;
  useEffect(() => {
    // `isFetchNextPageError` faz parte da condicao de parada: sem ele, a pagina
    // que recusa deixa `hasNextPage` verdadeiro e devolve `isFetchingNextPage` a
    // falso, o efeito redispara e a aba martela em laco a rota que acabou de
    // recusar — `app/queryClient.ts` usa `retry: false`, entao nada amortece.
    if (!hasNextPage || isFetchingNextPage || isFetchNextPageError) return;
    void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage]);

  return {
    // `undefined` ate o cursor fechar. Um seletor com metade do cadastro nao e
    // "menos opcoes": e quem opera procurando um nome, nao achando, e concluindo
    // que a pessoa nao existe — o mesmo desfecho que este laco existe para
    // fechar. Melhor a lista chegar um instante depois do que chegar mentindo.
    items:
      query.data === undefined || hasNextPage
        ? undefined
        : query.data.pages.flatMap((page) => read(page).items),
    isError: query.isError,
    refetch: () => void query.refetch(),
  };
}

/**
 * Prova de que o papel de quem esta na tela ja foi conferido.
 *
 * `SeriesCreator` chama `listTeachers` e `listEnrollments`, as duas exclusivas
 * de Super Admin, e ate agora dizia isso em lugar nenhum: ele era montado sem
 * condicao, e quem o alcancava dependia so de a rota `/series` estar fechada em
 * `routes.tsx`. Alcance fechado por fora e suposicao invisivel por dentro — e a
 * suposicao invisivel e o que reabre o buraco no dia em que alguem montar o
 * criador em outra tela, num painel, num modal de atalho.
 *
 * A marca e privada a este modulo e `superAdminViewer` e o unico construtor:
 * nao ha como passar `admin` sem ter conferido o papel, e o componente que
 * esquecer a conferencia **nao compila** em vez de abrir um formulario que so
 * sabe responder 403.
 *
 * A alternativa recusada foi um `if (me.role !== 'super_admin') return null`
 * solto antes do `return`. Ele fecharia este caso e nenhum outro: o proximo
 * lugar que montasse `SeriesCreator` comecaria de novo sem `if` nenhum, que e
 * exatamente a historia desta tela. E o mesmo motivo do `ownedOccurrence` no
 * servico de presenca.
 */
declare const roleCheckedHere: unique symbol;

interface SuperAdminViewer {
  readonly [roleCheckedHere]: true;
  /** O fuso de quem administra e so o palpite inicial da ancora; ver `SeriesCreator`. */
  readonly timezone: string;
}

function superAdminViewer(me: Me): SuperAdminViewer | undefined {
  if (me.role !== 'super_admin') return undefined;
  return { timezone: me.timezone } as SuperAdminViewer;
}

/**
 * Serie de aulas — a grade recorrente de um aluno.
 *
 * Ficou de fora explicitamente ate agora, e sem ela **nao havia como criar a
 * grade de um aluno pela interface**: matricula existia, aula avulsa existia, e
 * a recorrencia que gera as ocorrencias so nascia por semeadura.
 *
 * A decisao que a tela carrega, e que e a razao de o contrato ter dois campos em
 * vez de um instante: **a serie e ancorada no fuso do aluno**. `localTime` e
 * `anchorTimezone` viajam juntos e nunca sao convertidos para UTC aqui —
 * converter na criacao fixaria o horario de verao do dia em que a grade foi
 * montada, e a aula andaria uma hora sozinha duas vezes por ano.
 */
export function ClassSeriesPage() {
  const { t } = useTranslation();
  const meQuery = useGetMe();
  const me = meQuery.data;

  const seriesQuery = useWholeList(
    getListClassSeriesQueryKey(),
    (cursor, signal) =>
      listClassSeries(cursor === undefined ? { limit: PAGE_LIMIT } : { limit: PAGE_LIMIT, cursor }, {
        signal,
      }),
    (page) => page,
    !!me,
  );

  if (!me) return null;

  // "Nenhuma serie cadastrada" so pode ser dito depois que o cursor fechou e sem
  // erro no caminho: dita enquanto a varredura corre e um palpite, e dita no
  // lugar de uma falha ela troca a grade inexistente pela grade que nao foi
  // lida. `settledList` (features/schedule) e o mesmo derivador das duas filas
  // de mudanca de horario, e e ele que impede esse estado de ser escrito.
  const list = settledList<ClassSeries>({
    isError: seriesQuery.isError,
    // `useWholeList` ja segura `items` em `undefined` enquanto ha cursor: aqui a
    // varredura ou terminou, ou nem comecou.
    hasNextPage: false,
    items: seriesQuery.items,
  });
  const admin = superAdminViewer(me);

  return (
    <div className={styles.page}>
      <h1>{t('series.title')}</h1>
      <p className={styles.meta}>{t('series.anchorExplained')}</p>

      <section aria-label={t('series.list')}>
        <h2>{t('series.list')}</h2>
        <SettledListView state={list} empty={t('series.empty')} emptyClassName={styles.meta}>
          {(series) => (
            <ul className={styles.list}>
              {series.map((item) => (
                <SeriesCard key={item.id} series={item} />
              ))}
            </ul>
          )}
        </SettledListView>
      </section>

      {/*
        Sem a prova de papel o criador nao e montado, e nem `listTeachers` nem
        `listEnrollments` chegam a sair: quem nao pode criar tambem nao pede as
        listas que so o criador consome, e nao ve um formulario que o servidor
        recusaria. A lista de series continua para todo mundo que alcance a
        tela — quem recorta o que cada papel le e o servidor.
      */}
      {admin ? (
        <SeriesCreator admin={admin} onCreated={() => seriesQuery.refetch()} />
      ) : null}
    </div>
  );
}

function SeriesCard({ series }: { readonly series: ClassSeries }) {
  const { t } = useTranslation();
  const when = t('series.slot', {
    weekday: t(`series.weekday.${series.weekday}`),
    time: series.localTime,
  });

  return (
    // Nomeado pelo horario, e nao pelo professor: um professor tem varias
    // series, e o horario e o que distingue uma da outra na lista.
    <li className={styles.card} aria-label={when}>
      <p>
        <strong>{when}</strong> · {series.teacher.displayName}
      </p>
      {/*
        O fuso da ancora fica visivel. Sem ele, "09:00" nao diz nada a quem
        administra alunos em tres continentes.
      */}
      <p className={styles.meta}>
        {t('series.anchor', { timezone: series.anchorTimezone })} ·{' '}
        {t('series.validity', {
          from: series.validFrom,
          until: series.validUntil ?? t('series.openEnded'),
        })}
      </p>
    </li>
  );
}

/**
 * Serie nova.
 *
 * `enrollmentIds` aceita uma matricula ou duas: duas e a aula em dupla. Tres
 * seria uma turma, que o produto nao tem — e o servidor recusa com 422, entao a
 * tela nao deixa marcar a terceira.
 *
 * Recebe `admin` em vez de `timezone` solto: o fuso vem junto com a prova de
 * papel, e nao ha assinatura possivel que monte este formulario sem ela.
 */
function SeriesCreator({
  admin,
  onCreated,
}: {
  readonly admin: SuperAdminViewer;
  readonly onCreated: () => void;
}) {
  const { t } = useTranslation();
  const create = useCreateClassSeries();
  const teachersQuery = useWholeList<
    { items: readonly TeacherAdminDetail[]; nextCursor: string | null },
    TeacherAdminDetail
  >(
    getListTeachersQueryKey(),
    (cursor, signal) =>
      listTeachers(cursor === undefined ? { limit: PAGE_LIMIT } : { limit: PAGE_LIMIT, cursor }, {
        signal,
      }),
    (page) => page,
  );
  const enrollmentsQuery = useWholeList<
    { items: readonly Enrollment[]; nextCursor: string | null },
    Enrollment
  >(
    getListEnrollmentsQueryKey(),
    (cursor, signal) =>
      listEnrollments(cursor === undefined ? { limit: PAGE_LIMIT } : { limit: PAGE_LIMIT, cursor }, {
        signal,
      }),
    (page) => page,
  );

  const [teacherId, setTeacherId] = useState('');
  const [enrollmentIds, setEnrollmentIds] = useState<readonly string[]>([]);
  const [weekday, setWeekday] = useState('1');
  const [localTime, setLocalTime] = useState('09:00');
  // O fuso de quem administra e o palpite inicial, e nao a resposta: a ancora e
  // do ALUNO, e o campo fica editavel exatamente por isso.
  const [anchorTimezone, setAnchorTimezone] = useState(admin.timezone);
  const [validFrom, setValidFrom] = useState('');

  const teachers = teachersQuery.items ?? [];
  const enrollments = (enrollmentsQuery.items ?? []).filter((item) => item.active);
  // A leitura que falhou nao pode virar seletor curto e calado: sem isto, o 500
  // em `/v1/teachers` fica indistinguivel de "nao ha professor cadastrado", e o
  // formulario continua de pe convidando a montar uma serie sem professor.
  const selectorsFailed = teachersQuery.isError || enrollmentsQuery.isError;

  const validTime = /^([01][0-9]|2[0-3]):[0-5][0-9]$/.test(localTime);
  // O servidor recusa com 422 o que nao for identificador IANA, e 422 chegava
  // aqui como "erro inesperado" sem dizer qual campo estava errado. 'BRT',
  // 'GMT-3' e 'Sao Paulo' passavam pelo `length > 0`. Quem conhece a lista de
  // fusos e o proprio runtime; a mesma pergunta feita antes do envio nomeia o
  // campo em vez de gastar uma ida ao servidor.
  const validTimezone = isKnownTimezone(anchorTimezone);
  const canSubmit =
    teacherId.length > 0 &&
    enrollmentIds.length >= 1 &&
    enrollmentIds.length <= 2 &&
    validTime &&
    validTimezone &&
    validFrom.length > 0 &&
    !create.isPending;

  /**
   * Marcar a primeira matricula semeia a ancora com o fuso DO ALUNO.
   *
   * O campo nascia com o fuso de quem administra, e o desenho desta tela diz o
   * contrario em tres lugares — o texto de abertura, a dica do campo e o
   * comentario do estado. Para qualquer aluno fora do fuso do admin, aceitar o
   * palpite fazia a serie inteira nascer na hora civil errada e passar a seguir
   * o horario de verao do pais errado: duas vezes por ano a aula andava uma hora
   * sozinha enquanto o sistema a considerava parada.
   *
   * So a PRIMEIRA marcacao semeia. A segunda matricula e a dupla, e sobrescrever
   * a ancora nela trocaria em silencio um valor que o admin pode ter corrigido a
   * mao; quando os dois alunos da dupla vivem em fusos diferentes, a escolha e
   * de quem monta a grade e nao da ordem dos cliques.
   */
  const toggleEnrollment = (id: string, studentTimezone: string): void => {
    // A semeadura fica FORA do atualizador: o `setEnrollmentIds` pode ser
    // reexecutado pelo React, e efeito dentro dele acontece duas vezes.
    if (!enrollmentIds.includes(id) && enrollmentIds.length === 0) {
      setAnchorTimezone(studentTimezone);
    }
    setEnrollmentIds((current) =>
      current.includes(id)
        ? current.filter((candidate) => candidate !== id)
        : current.length >= 2
          ? current
          : [...current, id],
    );
  };

  return (
    <section aria-label={t('series.create')}>
      <h2>{t('series.create')}</h2>
      <form
        className={styles.form}
        onSubmit={(event) => {
          event.preventDefault();
          if (!canSubmit) return;
          create.mutate(
            {
              data: {
                teacherId,
                enrollmentIds: [...enrollmentIds],
                weekday: Number(weekday),
                localTime,
                anchorTimezone,
                validFrom,
              },
            },
            {
              onSuccess: () => {
                setEnrollmentIds([]);
                onCreated();
              },
            },
          );
        }}
      >
        {create.isSuccess ? <Alert tone="success">{t('series.created')}</Alert> : null}
        {create.isError ? <Alert tone="error">{t('error.unexpected')}</Alert> : null}
        {selectorsFailed ? <Alert tone="error">{t('error.loadFailed')}</Alert> : null}

        <label className={styles.selectLabel}>
          {t('series.teacher')}
          <select value={teacherId} onChange={(event) => setTeacherId(event.target.value)}>
            <option value="">{t('series.teacherPlaceholder')}</option>
            {teachers.map((teacher) => (
              <option key={teacher.personId} value={teacher.personId}>
                {teacher.displayName}
              </option>
            ))}
          </select>
        </label>

        <fieldset className={styles.fieldset}>
          <legend>{t('series.enrollments')}</legend>
          <p className={styles.meta}>{t('series.enrollmentsHint')}</p>
          {enrollments.map((enrollment) => (
            <label key={enrollment.id} className={styles.checkLabel}>
              <input
                type="checkbox"
                checked={enrollmentIds.includes(enrollment.id)}
                onChange={() => toggleEnrollment(enrollment.id, enrollment.student.timezone)}
              />
              {enrollment.student.displayName} · {enrollment.student.timezone}
            </label>
          ))}
        </fieldset>

        <label className={styles.selectLabel}>
          {t('series.weekdayLabel')}
          <select value={weekday} onChange={(event) => setWeekday(event.target.value)}>
            {WEEKDAYS.map((day) => (
              <option key={day} value={String(day)}>
                {t(`series.weekday.${day}`)}
              </option>
            ))}
          </select>
        </label>

        <Field
          label={t('series.localTime')}
          type="time"
          required
          value={localTime}
          onChange={(event) => setLocalTime(event.target.value)}
          error={validTime ? undefined : t('series.localTimeInvalid')}
        />
        <Field
          label={t('series.anchorTimezone')}
          required
          hint={t('series.anchorTimezoneHint')}
          value={anchorTimezone}
          onChange={(event) => setAnchorTimezone(event.target.value)}
          // A dica repetida como erro, e nao uma frase nova: ela ja diz
          // exatamente o que falta ("o fuso do aluno, no formato IANA"), e o
          // catalogo de traducao atravessa as tres trilhas e nao e desta onda.
          error={validTimezone ? undefined : t('series.anchorTimezoneHint')}
        />
        <Field
          label={t('series.validFrom')}
          type="date"
          required
          value={validFrom}
          onChange={(event) => setValidFrom(event.target.value)}
        />

        <div>
          <Button variant="primary" type="submit" disabled={!canSubmit} loading={create.isPending}>
            {t('series.createAction')}
          </Button>
        </div>
      </form>
    </section>
  );
}
